import React, { useEffect, useState } from 'react';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// Must match backend ALLOWED_ASSIGNMENT_REASONS exactly
// (bq/backend/app/api/checkIn/services/room_assignment.py)
const ASSIGNMENT_REASONS = [
  { value: '', label: '-- Select reason --' },
  { value: 'complimentary_upgrade', label: 'Complimentary Upgrade (rooms full)' },
  { value: 'price_match', label: 'Price Match (same price, better room)' },
  { value: 'paid_upgrade', label: 'Paid Upgrade (guest paid extra)' },
  { value: 'downgrade', label: 'Downgrade (assigned lower room)' },
  { value: 'substitution', label: 'Substitution (similar room type)' },
  { value: 'vip', label: 'VIP Guest Upgrade' },
  { value: 'maintenance', label: 'Room Maintenance Issue' },
  { value: 'guest_request', label: 'Guest Requested Change' },
];

const RoomAssignmentModal = ({ reservation, onClose, onAssignRoom }) => {
  const [roomNumber, setRoomNumber] = useState('');
  const [availableRooms, setAvailableRooms] = useState([]);
  const [assignmentReason, setAssignmentReason] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');

  useEffect(() => {
    const fetchAvailableRooms = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/availability`, {
          method: 'GET',
          headers: { accept: 'application/json' },
        });
        const data = await response.json();
        setAvailableRooms(data?.available_rooms || []);
      } catch (error) {
        console.error('Error fetching available rooms:', error);
        setAvailableRooms([]);
      }
    };
    fetchAvailableRooms();
  }, []);

  const bookedRoomTypeName = reservation.booked_room_type?.roomtypename || reservation.roomtypename;
  const selectedRoom = availableRooms.find((r) => String(r.roomnumber) === String(roomNumber));
  const roomTypeChanged = selectedRoom && selectedRoom.room_type !== bookedRoomTypeName;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (roomTypeChanged && !assignmentReason) {
      return;
    }
    if (roomTypeChanged && !assignmentNotes.trim()) {
      return;
    }
    onAssignRoom(
      reservation.bookingid,
      roomNumber,
      roomTypeChanged ? assignmentReason : null,
      roomTypeChanged ? assignmentNotes.trim() : null
    );
  };

  const canSubmit =
    roomNumber && (!roomTypeChanged || (assignmentReason && assignmentNotes.trim()));

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-50">
      <div className="max-w-full w-[450px] mx-auto p-6 bg-white rounded-lg shadow-lg m-0">
        <h2 className="text-2xl font-semibold text-center mb-6">Assign Room</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Booking ID Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reservation Number
            </label>
            <input
              type="text"
              value={reservation.orderid}
              readOnly
              disabled
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Booked Room Type</label>
            <input
              type="text"
              value={bookedRoomTypeName || ''}
              readOnly
              disabled
              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 focus:outline-none"
            />
          </div>

          {/* Room Number Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Room</label>
            <select
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none"
              required
            >
              <option value="">-- Select an available room --</option>
              {availableRooms.map((room) => (
                <option key={room.roomid} value={room.roomnumber}>
                  {room.roomnumber} — {room.room_type}
                </option>
              ))}
            </select>
          </div>

          {roomTypeChanged && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                Selected room is <strong>{selectedRoom.room_type}</strong>, different from the
                booked <strong>{bookedRoomTypeName}</strong>. A reason and notes are required.
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Room Change
                </label>
                <select
                  value={assignmentReason}
                  onChange={(e) => setAssignmentReason(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none"
                  required
                >
                  {ASSIGNMENT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={assignmentNotes}
                  onChange={(e) => setAssignmentNotes(e.target.value)}
                  rows="2"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none"
                  placeholder="Explain the room change"
                  required
                />
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-between gap-4">
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full py-3 text-white rounded-lg focus:outline-none transition ${
                canSubmit ? 'bg-black hover:text-white' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              Assign
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 bg-red-500 text-white rounded-lg focus:outline-none hover:text-white transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoomAssignmentModal;
